import styled from 'styled-components'

const PageBackground = styled.div<{ $brand: string; $accent: string }>`
  --qc-brand: ${props => props.$brand};
  --qc-accent: ${props => props.$accent};
  min-height: 100vh;
  width: 100%;
  background: #f6f4fb;
  padding: 40px 16px;
  display: flex;
  justify-content: center;
`

const PageContent = styled.div`
  width: 100%;
  max-width: 760px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const BrandHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`

const BrandLogo = styled.img`
  max-height: 100px;
  max-width: 100%;
`

const BrandName = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1f1b2e;
`

const SentBy = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: #5b5667;
`

const QuoteCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 6px 24px rgba(31, 27, 46, 0.08);
`

const QuoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
`

const QuoteTitle = styled.h1`
  font-size: 24px;
  color: #1f1b2e;
  margin: 0;
`

const VersionTag = styled.span`
  font-size: 13px;
  color: #6b6577;
  white-space: nowrap;
`

const IntroText = styled.p`
  font-size: 15px;
  color: #4b4658;
  line-height: 1.6;
  margin: 0 0 24px;
  white-space: pre-wrap;
`

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;

  thead th {
    text-align: left;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b6577;
    border-bottom: 2px solid #ece9f3;
    padding: 8px 10px;
  }

  tbody td {
    padding: 12px 10px;
    border-bottom: 1px solid #f0eef6;
    vertical-align: top;
    color: #1f1b2e;
    font-size: 14px;
  }

  td.numeric,
  th.numeric {
    text-align: right;
    white-space: nowrap;
  }

  td.select-cell,
  th.select-cell {
    width: 44px;
    text-align: center;
  }

  input[type='checkbox'],
  input[type='radio'] {
    width: 18px;
    height: 18px;
    accent-color: var(--qc-brand);
    cursor: pointer;
  }

  input:disabled {
    cursor: default;
  }

  .locked-tag {
    font-size: 16px;
    color: #a49dbb;
  }

  tbody tr.excluded td {
    color: #a49dbb;
  }

  tbody tr.excluded td.numeric {
    text-decoration: line-through;
  }

  .needs-hint {
    display: block;
    font-size: 12px;
    color: #9a94a8;
    margin-top: 4px;
    font-style: italic;
  }

  @media (max-width: 600px) {
    thead {
      display: none;
    }

    tbody tr {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      column-gap: 12px;
      padding: 14px 0;
      border-bottom: 1px solid #f0eef6;
    }

    tbody td {
      padding: 0;
      border-bottom: none;
    }

    td.select-cell {
      flex: 0 0 100%;
      width: auto;
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }

    td.select-cell::after {
      content: 'Include';
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #6b6577;
    }

    td.description-cell {
      flex: 0 0 100%;
      font-weight: 600;
      margin-bottom: 10px;
    }

    td.numeric {
      flex: 1;
      text-align: left;
      white-space: nowrap;
      font-size: 13px;
    }

    td.numeric:last-child {
      text-align: right;
    }

    td.numeric::before {
      content: attr(data-label);
      display: block;
      font-weight: 400;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #6b6577;
      margin-bottom: 2px;
    }
  }
`

const SelectionHint = styled.p`
  font-size: 14px;
  color: #4b4658;
  line-height: 1.5;
  margin: 0 0 4px;

  strong {
    color: #1f1b2e;
  }
`

const TotalsBlock = styled.div`
  width: 100%;
  max-width: 320px;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const TotalsRow = styled.div<{ $grand?: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  font-size: ${props => (props.$grand ? '18px' : '14px')};
  font-weight: ${props => (props.$grand ? 700 : 500)};
  color: ${props => (props.$grand ? '#1f1b2e' : '#4b4658')};
  padding-top: ${props => (props.$grand ? '10px' : '0')};
  border-top: ${props => (props.$grand ? '2px solid #ece9f3' : 'none')};
`

const NotesBlock = styled.div`
  margin-top: 28px;
  padding-top: 18px;
  border-top: 1px solid #f0eef6;

  h3 {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b6577;
    margin: 0 0 6px;
  }

  p {
    margin: 0;
    color: #4b4658;
    white-space: pre-wrap;
    line-height: 1.6;
  }
`

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-top: 8px;
`

const PdfLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--qc-brand);
  text-decoration: none;
`

const PrimaryButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 12px 22px;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  background: var(--qc-brand);
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const SuccessButton = styled(PrimaryButton)`
  background: #047857;
`

const OutlineButton = styled.button`
  border: 1px solid #d5d0e0;
  border-radius: 10px;
  padding: 12px 22px;
  font-size: 15px;
  font-weight: 600;
  color: #4b4658;
  background: #ffffff;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #f3f1f8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const StateCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 40px 32px;
  box-shadow: 0 6px 24px rgba(31, 27, 46, 0.08);
  text-align: center;
  color: #4b4658;
  font-size: 16px;
  max-width: 480px;
  margin: 60px auto;
`

const RequestLinkForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
  text-align: left;

  p {
    margin: 0;
    font-size: 14px;
    color: #5b5667;
    text-align: center;
  }
`

const RequestSentNote = styled.p`
  margin: 20px 0 0;
  font-size: 15px;
  font-weight: 600;
  color: #047857;
`

const RequestErrorNote = styled.p`
  margin: 0;
  font-size: 13px;
  color: #b91c1c;
  text-align: center;
`

const CommentThread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0;
`

const CommentBubble = styled.div<{ $isClient: boolean }>`
  align-self: ${props => (props.$isClient ? 'flex-end' : 'flex-start')};
  max-width: 80%;
  background: ${props =>
    props.$isClient ? 'var(--qc-accent)' : '#f3f1f8'};
  border-radius: 12px;
  padding: 10px 14px;
`

const CommentAuthor = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b6577;
  margin-bottom: 2px;
`

const CommentBody = styled.div`
  font-size: 14px;
  color: #1f1b2e;
  white-space: pre-wrap;
`

const CommentTextarea = styled.textarea`
  width: 100%;
  min-height: 72px;
  resize: vertical;
  border: 1px solid #d5d0e0;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  color: #1f1b2e;

  &:focus {
    outline: none;
    border-color: var(--qc-brand);
  }
`

const ResultBanner = styled.div<{ $tone: 'approved' | 'declined' }>`
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 15px;
  font-weight: 600;
  color: ${props => (props.$tone === 'approved' ? '#047857' : '#b91c1c')};
  background: ${props => (props.$tone === 'approved' ? '#d1fae5' : '#fee2e2')};
`

const LockedBanner = styled.div`
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 15px;
  font-weight: 600;
  color: #4b5563;
  background: #f3f4f6;
`

const PreviewBanner = styled.div`
  background: #1f1b2e;
  color: #ffffff;
  border-radius: 12px;
  padding: 12px 18px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`

const PreviewResetButton = styled.button`
  margin-left: 12px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`

const BriefDeck = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const BriefSlide = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(31, 27, 46, 0.12);
`

const BriefStage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: center;
`

const BriefNav = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const BriefNavButton = styled.button`
  border: 1px solid #d5d0e0;
  background: #ffffff;
  color: #1f1b2e;
  border-radius: 10px;
  padding: 8px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--qc-brand);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`

const BriefCounter = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #6b6577;
  min-width: 56px;
  text-align: center;
`

const CommentEmpty = styled.div`
  font-size: 14px;
  color: #6b6577;
  padding: 4px 0;
`

export {
  PageBackground,
  PageContent,
  BrandHeader,
  BrandLogo,
  BrandName,
  SentBy,
  BriefDeck,
  BriefSlide,
  BriefStage,
  BriefNav,
  BriefNavButton,
  BriefCounter,
  QuoteCard,
  QuoteHeader,
  QuoteTitle,
  VersionTag,
  IntroText,
  ItemsTable,
  SelectionHint,
  TotalsBlock,
  TotalsRow,
  NotesBlock,
  ActionsRow,
  PdfLink,
  PrimaryButton,
  SuccessButton,
  OutlineButton,
  StateCard,
  RequestLinkForm,
  RequestSentNote,
  RequestErrorNote,
  CommentThread,
  CommentBubble,
  CommentAuthor,
  CommentBody,
  CommentTextarea,
  CommentEmpty,
  ResultBanner,
  LockedBanner,
  PreviewBanner,
  PreviewResetButton,
}
